import { GAME_CONFIG } from "../config/gameConfig.js";

/**
 * Sniff scent-mark layer
 *
 * Invisible marks (tileset overlay) that flash on Sniff press,
 * then slowly fade — paths, traps, puzzle hints.
 *
 * Layer symbols:
 *   p  path trail
 *   !  trap / danger
 *   ?  puzzle hint
 *   .  empty
 */

export const SCENT_TYPES = {
    path: {
        id: "path",
        color: [80, 255, 160],
        size: 5,
    },
    trap: {
        id: "trap",
        color: [255, 90, 70],
        size: 6,
    },
    hint: {
        id: "hint",
        color: [100, 220, 255],
        size: 5,
    },
};

const SYMBOL_TO_TYPE = {
    p: "path",
    P: "path",
    "!": "trap",
    "?": "hint",
    h: "hint",
    H: "hint",
};

export function clearScentMarks() {
    destroyAll("scent_mark");
}

export function spawnScentMark(worldPos, typeId = "path") {
    const type = SCENT_TYPES[typeId] || SCENT_TYPES.path;
    const [cr, cg, cb] = type.color;

    const mark = add([
        circle(type.size),
        pos(worldPos),
        anchor("center"),
        color(cr, cg, cb),
        opacity(0),
        z(9200),
        "scent_mark",
        {
            scentType: typeId,
            revealed: 0,
        },
    ]);

    mark.halo = mark.add([
        circle(type.size + 4),
        anchor("center"),
        color(cr, cg, cb),
        opacity(0),
        z(-1),
    ]);

    return mark;
}

/**
 * Spawn scent marks from an ASCII overlay matching the room size.
 */
export function loadSniffLayer(mapRows, tileWidth, tileHeight) {
    clearScentMarks();

    const tw = tileWidth ?? GAME_CONFIG.level.tileWidth;
    const th = tileHeight ?? GAME_CONFIG.level.tileHeight;

    for (let y = 0; y < mapRows.length; y++) {
        const row = mapRows[y];
        for (let x = 0; x < row.length; x++) {
            const typeId = SYMBOL_TO_TYPE[row[x]];
            if (!typeId) continue;

            const center = vec2(x * tw + tw / 2, y * th + th / 2);
            spawnScentMark(center, typeId);
        }
    }
}

function updateMarkVisual(mark, strength) {
    const type = SCENT_TYPES[mark.scentType] || SCENT_TYPES.path;
    const [cr, cg, cb] = type.color;

    mark.color = rgb(cr, cg, cb);
    mark.opacity = strength * 0.85;

    if (mark.halo && mark.halo.exists?.() !== false) {
        mark.halo.opacity = strength * 0.25;
        mark.halo.color = mark.color;
    }
}

/**
 * Flash nearby scent marks — call on Sniff key press.
 */
export function triggerSniff(player) {
    if (!player) return;

    const radius = GAME_CONFIG.sniff.radius ?? 110;
    const marks = get("scent_mark", { recursive: true });

    for (const mark of marks) {
        const dist = mark.pos.dist(player.pos);
        if (dist > radius) continue;

        const t = 1 - dist / radius;
        mark.revealed = Math.max(mark.revealed, t * t);
    }
}

/**
 * Call each frame while the game scene runs — slowly fades marks.
 */
export function updateSniffMarks() {
    const fadeDuration = GAME_CONFIG.sniff.fadeDuration ?? 2.5;
    const fadeRate = 1 / Math.max(0.05, fadeDuration);
    const marks = get("scent_mark", { recursive: true });

    for (const mark of marks) {
        if (mark.revealed > 0) {
            mark.revealed = Math.max(0, mark.revealed - fadeRate * dt());
        }
        updateMarkVisual(mark, mark.revealed);
    }
}
